import React, { useEffect, useState } from 'react';
import { Users, UserPlus, Trash2, Loader2, ShieldCheck, Eye, Crown, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { useVendorContext } from './VendorAdminLayout';
import { vendorTeamApi } from '../services/api';
import { toast } from 'sonner';

/**
 * VendorTeamPage — invite / manage / remove store team members.
 * Roles:
 *   • owner   — implicit, backed by `vendors.owner_user_id` / `owner_user_ids[]`
 *   • manager — read+write orders + menu
 *   • viewer  — read-only
 */
const roleMeta = {
  owner:   { label: 'Owner',   icon: Crown,        cls: 'bg-amber-100 text-amber-700' },
  manager: { label: 'Manager', icon: ShieldCheck,  cls: 'bg-emerald-100 text-emerald-700' },
  viewer:  { label: 'Viewer',  icon: Eye,          cls: 'bg-slate-100 text-slate-700' },
};

const VendorTeamPage = () => {
  const { impersonating, vendor_id } = useVendorContext();
  const [state, setState] = useState({ loading: true, data: null });
  const [inviteOpen, setInviteOpen] = useState(false);

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const data = await vendorTeamApi.list(impersonating ? vendor_id : undefined);
      setState({ loading: false, data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load team');
      setState({ loading: false, data: null });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vendor_id]);

  const isOwner = state.data?.role === 'owner';

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <Users className="h-6 w-6 text-emerald-600" /> Store team
          </h1>
          <p className="text-slate-500 mt-1">
            Add staff logins bound to this store — view-only or manage-orders access.
          </p>
        </div>
        {isOwner && (
          <Button onClick={() => setInviteOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="team-invite-btn">
            <UserPlus className="h-4 w-4 mr-1.5" /> Invite team member
          </Button>
        )}
      </div>

      {state.loading ? (
        <div className="mt-8 text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="mt-6 space-y-3" data-testid="team-list">
          {/* Owners */}
          {(state.data?.owners || []).map((o) => (
            <TeamRow key={o.user_id} row={o} isOwner={isOwner} showActions={false} />
          ))}
          {(state.data?.team || []).map((m) => (
            <TeamRow
              key={m.email}
              row={m}
              isOwner={isOwner}
              onRoleChange={async (role) => {
                try { await vendorTeamApi.updateRole(m.email, role, impersonating ? vendor_id : undefined); toast.success('Role updated'); load(); }
                catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
              }}
              onRemove={async () => {
                if (!window.confirm(`Remove ${m.email} from this store?`)) return;
                try { await vendorTeamApi.remove(m.email, impersonating ? vendor_id : undefined); toast.success('Team member removed'); load(); }
                catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
              }}
            />
          ))}
          {(state.data?.team || []).length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-500" data-testid="team-empty">
              No team members yet. {isOwner ? 'Invite someone to help manage orders.' : 'Ask the store owner to add you.'}
            </div>
          )}
        </div>
      )}

      <InviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvite={async ({ email, role, name }) => {
          try {
            const { pending } = await vendorTeamApi.invite({ email, role, name }, impersonating ? vendor_id : undefined);
            toast.success(pending
              ? `Invite saved — ${email} will be added when they sign up.`
              : `${email} added to the team.`);
            setInviteOpen(false); load();
          } catch (e) {
            toast.error(e?.response?.data?.detail || 'Invite failed');
          }
        }}
      />
    </div>
  );
};

const TeamRow = ({ row, isOwner, showActions = true, onRoleChange, onRemove }) => {
  const meta = roleMeta[row.role] || roleMeta.viewer;
  const Icon = meta.icon;
  const pending = !row.user_id;
  return (
    <div className={`bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-4 ${pending ? 'ring-1 ring-amber-200 bg-amber-50/40' : ''}`} data-testid={`team-row-${row.email}`}>
      <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-none ${meta.cls}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          {row.name || row.email}
          <Badge className={`text-[10px] uppercase tracking-wider border-0 ${meta.cls}`}>{meta.label}</Badge>
          {pending && <Badge className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-700 border-0">Pending signup</Badge>}
        </div>
        <div className="text-xs text-slate-500 truncate flex items-center gap-1"><Mail className="h-3 w-3" />{row.email}</div>
      </div>
      {showActions && isOwner && (
        <div className="flex items-center gap-2 flex-none">
          <Select defaultValue={row.role} onValueChange={onRoleChange}>
            <SelectTrigger className="h-9 w-32" data-testid={`team-role-${row.email}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="manager">Manager</SelectItem>
              <SelectItem value="viewer">Viewer</SelectItem>
            </SelectContent>
          </Select>
          <Button
            onClick={onRemove}
            variant="outline"
            size="sm"
            className="text-red-600 hover:bg-red-50"
            data-testid={`team-remove-${row.email}`}
            aria-label={`Remove ${row.email}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

const InviteDialog = ({ open, onClose, onInvite }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('manager');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!email.trim()) return;
    setBusy(true);
    await onInvite({ email: email.trim(), role, name: name.trim() });
    setBusy(false); setEmail(''); setName('');
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Invite a team member</DialogTitle>
          <DialogDescription>They'll be able to sign in and access this store only.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</label>
            <Input type="email" placeholder="staff@yourstore.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="team-invite-email" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Name (optional)</label>
            <Input placeholder="e.g. Adaeze O." value={name} onChange={(e) => setName(e.target.value)} data-testid="team-invite-name" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Role</label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="team-invite-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="manager">Manager — accept orders, edit menu</SelectItem>
                <SelectItem value="viewer">Viewer — read-only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={submit} disabled={!email.trim() || busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="team-invite-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send invite'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VendorTeamPage;
